import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  Box,
  Button,
  Checkbox,
  Flex,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { ReactGrid } from "@silevis/reactgrid";

import { AppLayout } from "@/components/app-layout";
import { PlannerInlinePanel } from "@/components/planner-inline-panel";
import {
  saveWeeklyPlannerAllocation,
  saveWeeklyPlannerAvailableHours,
  saveWeeklyPlannerDeveloperOrder,
  saveWeeklyPlannerProjectOrder,
  saveWeeklyPlannerProjectMetadata,
  saveWeeklyPlannerRowNote,
  type ProjectListItem,
  type DeveloperListItem,
  fetchWeeklyPlannerWithRetry,
} from "@/lib/api";
import {
  type ActiveCollaboratorCells,
  buildGridContract,
  WEEKLY_PLANNER_PANEL_HEIGHT_DEFAULT,
  WEEKLY_PLANNER_ROW_HEIGHT,
  weeklyPlannerPanelRowId,
  type ModuleKey,
} from "@/lib/grid-contract";
import { usePage } from "@inertiajs/react";
import { currentWeekStartIso, shiftWeek, weekStartFromDateInput } from "@/lib/date-helpers";
import type { SharedProps } from "@/lib/types";
import { useRealtimeCollaboration } from "@/hooks/use-realtime-collaboration";
import { useRepositoryActivity } from "@/hooks/use-repository-activity";
import {
  plannerApiViewForModule,
  plannerModuleActive,
  useWeeklyPlanner,
} from "@/hooks/use-weekly-planner";
import type { SharedProps } from "@/lib/types";
import { edgeFromPoint } from "@/lib/drag-reorder";
import {
  canDropProjectRow,
  nextDeveloperOrderForDrop,
  nextProjectOrderForDrop,
} from "@/lib/weekly-planner-drag-reorder";

type PbrainShellProps = SharedProps & {
  projects: ProjectListItem[];
  developers: DeveloperListItem[];
};

type SaveState = "idle" | "saving" | "saved" | "error";
type ProjectRowHandle = { rowId: string; rowLabel: string; top: number; height: number };
type DeveloperColumnHandle = { userId: number; label: string; left: number; width: number; top: number; height: number };
type DropTargetEdge = "top" | "bottom" | null;
type DropTargetHorizontalEdge = "left" | "right" | null;

export function PbrainShell({ auth, projects, developers }: PbrainShellProps) {
  const page = usePage<SharedProps>();
  const userRoles = auth.user?.roles || [];
  const [activeModule, setActiveModule] = useState<ModuleKey>(initialModuleFromQuery());
  const initialWeekStart =
    page.props.app_clock?.current_week_start_on ?? currentWeekStartIso();
  const [selectedWeekStartOn, setSelectedWeekStartOn] = useState(initialWeekStart);

  useEffect(() => {
    const week = page.props.app_clock?.current_week_start_on;
    if (week) setSelectedWeekStartOn(week);
  }, [page.props.app_clock?.current_week_start_on, page.props.app_clock?.offset_weeks]);

  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);
  const [draggedProjectRowId, setDraggedProjectRowId] = useState<string | null>(null);
  const [dropTargetProjectRowId, setDropTargetProjectRowId] = useState<string | null>(null);
  const [dropTargetEdge, setDropTargetEdge] = useState<DropTargetEdge>(null);
  const [draggedDeveloperId, setDraggedDeveloperId] = useState<number | null>(null);
  const [dropTargetDeveloperId, setDropTargetDeveloperId] = useState<number | null>(null);
  const [dropTargetDeveloperEdge, setDropTargetDeveloperEdge] = useState<DropTargetHorizontalEdge>(null);
  const [allocationSaving, setAllocationSaving] = useState(false);
  const [openPanelRowId, setOpenPanelRowId] = useState<string | null>(null);
  const [noteDueDate, setNoteDueDate] = useState("");
  const [taskNotes, setTaskNotes] = useState("");
  const [invoicingNotes, setInvoicingNotes] = useState("");
  const [projectedEndDate, setProjectedEndDate] = useState("");
  const [projectOwnerId, setProjectOwnerId] = useState("");
  const [projectManagerId, setProjectManagerId] = useState("");
  const [frontendLeadId, setFrontendLeadId] = useState("");
  const [backendLeadId, setBackendLeadId] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesSaveState, setNotesSaveState] = useState<SaveState>("idle");
  const [metadataSaveState, setMetadataSaveState] = useState<SaveState>("idle");
  const [measuredPanelHeight, setMeasuredPanelHeight] = useState<number | null>(null);
  const notesSaveInFlightRef = useRef(false);
  const pendingNoteSaveRef = useRef(false);
  const noteSaveGenerationRef = useRef(0);
  const lastAppliedNoteSaveGenerationRef = useRef(0);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const clientRequestIdRef = useRef(`wp-${Math.random().toString(36).slice(2, 10)}`);
  const {
    weeklyPlanner,
    setWeeklyPlanner,
    plannerFetching,
    plannerError,
    setPlannerError,
    syncing,
    syncMessage,
    setSyncMessage,
    copyingPreviousWeek,
    includeArchived,
    setIncludeArchived,
    handleSync,
    handleCopyPreviousWeekConfirm,
  } = useWeeklyPlanner({
    activeModule,
    selectedWeekStartOn,
    sourceClientId: clientRequestIdRef.current,
  });
  const { presenceUsers, realtimeMessage, publishActiveCellChanged } = useRealtimeCollaboration({
    activeModule,
    weeklyPlanner,
    sourceClientId: clientRequestIdRef.current,
    onRemotePlannerChanged: setWeeklyPlanner,
    onRemoteMyProjectsChanged: async (weekStartOn: string) =>
      fetchWeeklyPlannerWithRetry(weekStartOn, "my_projects"),
  });

  const activeCollaboratorCells = useMemo<ActiveCollaboratorCells>(() => {
    const cells: ActiveCollaboratorCells = {};
    const currentClientId = clientRequestIdRef.current;
    presenceUsers.forEach((presenceUser) => {
      const activeCell = presenceUser.active_cell;
      if (!activeCell || presenceUser.source_client_id === currentClientId) return;
      cells[`${activeCell.row_id}::${activeCell.column_id}`] = presenceUser.name;
    });
    return cells;
  }, [presenceUsers]);

  const gridContract = useMemo(
    () =>
      buildGridContract(
        activeModule,
        projects,
        developers,
        weeklyPlanner,
        openPanelRowId,
        measuredPanelHeight,
        activeCollaboratorCells,
        {
          draggingRowId: draggedProjectRowId,
          dropTargetRowId: dropTargetProjectRowId,
          dropTargetEdge,
          draggingDeveloperId: draggedDeveloperId,
          dropTargetDeveloperId,
          dropTargetDeveloperEdge,
        },
      ),
    [
      activeModule,
      projects,
      developers,
      weeklyPlanner,
      openPanelRowId,
      measuredPanelHeight,
      activeCollaboratorCells,
      draggedProjectRowId,
      dropTargetProjectRowId,
      dropTargetEdge,
      draggedDeveloperId,
      dropTargetDeveloperId,
      dropTargetDeveloperEdge,
    ]
  );

  const openRowTarget = openPanelRowId
    ? gridContract.meta?.plannerRowsById?.[openPanelRowId]
    : undefined;
  const openRowData =
    openRowTarget && weeklyPlanner
      ? weeklyPlanner.rows.find(
          (row) =>
            row.row_kind === openRowTarget.row_kind &&
            row.row_entity_id === openRowTarget.row_entity_id,
        )
      : undefined;
  const panelRepositories = openRowData?.repository_links || [];
  const { panelPullRequests, panelCommits, repositoryActivityLoading, repositoryActivityError } =
    useRepositoryActivity(openRowData);
  const canEditPanelProjectMetadata = Boolean(
    (gridContract.meta?.canEditProjectMetadata ??
      (userRoles.includes("administrator") || userRoles.includes("project_manager"))) &&
    weeklyPlanner?.editable,
  );
  const panelTop = useMemo(() => {
    if (!openPanelRowId || !openRowData) return null;

    const panelRowId = gridContract.meta?.panelRowId || weeklyPlannerPanelRowId(openPanelRowId);
    let top = 0;
    for (const row of gridContract.rows) {
      if (String(row.rowId) === panelRowId) return top;
      top += row.height ?? WEEKLY_PLANNER_ROW_HEIGHT;
    }

    return null;
  }, [gridContract, openPanelRowId, openRowData]);
  const canReorderProjectRows =
    plannerModuleActive(activeModule) &&
    Boolean(weeklyPlanner?.editable) &&
    (userRoles.includes("administrator") || userRoles.includes("project_manager"));
  const canReorderDeveloperColumns =
    plannerModuleActive(activeModule) &&
    Boolean(weeklyPlanner?.editable) &&
    (userRoles.includes("administrator") || userRoles.includes("project_manager")) &&
    (weeklyPlanner?.developers.length || 0) > 1;

  const projectRowHandles = useMemo<ProjectRowHandle[]>(() => {
    if (!canReorderProjectRows || !weeklyPlanner) return [];

    const rowLabelByPlannerRowId = weeklyPlanner.rows.reduce<Record<string, string>>((acc, row) => {
      if (row.row_kind !== "project") return acc;
      acc[`planner-project-${row.row_entity_id}`] = row.row_label;
      return acc;
    }, {});

    const handles: ProjectRowHandle[] = [];
    let topOffset = 0;

    gridContract.rows.forEach((row) => {
      const rowId = String(row.rowId);
      const rowHeight = row.height ?? WEEKLY_PLANNER_ROW_HEIGHT;
      const rowMeta = gridContract.meta?.plannerRowsById?.[rowId];

      if (rowMeta?.row_kind === "project" && rowMeta.project_group_id) {
        handles.push({
          rowId,
          rowLabel: rowLabelByPlannerRowId[rowId] || rowId,
          top: topOffset,
          height: rowHeight,
        });
      }

      topOffset += rowHeight;
    });

    return handles;
  }, [canReorderProjectRows, gridContract.meta?.plannerRowsById, gridContract.rows, weeklyPlanner]);

  const developerColumnHandles = useMemo<DeveloperColumnHandle[]>(() => {
    if (!canReorderDeveloperColumns || !weeklyPlanner) return [];

    const nameByDeveloperId = weeklyPlanner.developers.reduce<Record<number, string>>((acc, developer) => {
      acc[developer.id] = developer.name;
      return acc;
    }, {});
    const headerRowHeight = gridContract.rows[0]?.height ?? WEEKLY_PLANNER_ROW_HEIGHT;
    const handles: DeveloperColumnHandle[] = [];
    let leftOffset = 0;

    gridContract.columns.forEach((column) => {
      const columnId = String(column.columnId);
      const width = Number(column.width ?? 0);

      if (columnId.startsWith("dev-")) {
        const developerId = Number(columnId.replace("dev-", ""));
        if (!Number.isNaN(developerId) && nameByDeveloperId[developerId]) {
          handles.push({
            userId: developerId,
            label: nameByDeveloperId[developerId],
            left: leftOffset,
            width,
            top: 0,
            height: headerRowHeight,
          });
        }
      }

      leftOffset += width;
    });

    return handles;
  }, [canReorderDeveloperColumns, gridContract.columns, gridContract.rows, weeklyPlanner]);

  const openRowKey =
    openRowData && weeklyPlanner
      ? `${weeklyPlanner.week_start_on}:${openRowData.row_kind}:${openRowData.row_entity_id}`
      : null;

  useEffect(() => {
    if (!openRowData) return;
    noteSaveGenerationRef.current = 0;
    lastAppliedNoteSaveGenerationRef.current = 0;
    setNoteDueDate(openRowData.due_date ? openRowData.due_date.slice(0, 10) : "");
    setTaskNotes(openRowData.task_notes || "");
    setInvoicingNotes(openRowData.invoicing_notes || "");
    setProjectedEndDate(
      openRowData.projected_end_date ? openRowData.projected_end_date.slice(0, 10) : "",
    );
    setProjectOwnerId(openRowData.project_owner_id?.toString() || "");
    setProjectManagerId(openRowData.project_manager_id?.toString() || "");
    setFrontendLeadId(openRowData.frontend_lead_id?.toString() || "");
    setBackendLeadId(openRowData.backend_lead_id?.toString() || "");
    setNotesDirty(false);
    setNotesSaveState("idle");
    setMetadataSaveState("idle");
  }, [
    openRowKey,
    openRowData?.due_date,
    openRowData?.projected_end_date,
    openRowData?.project_owner_id,
    openRowData?.project_manager_id,
    openRowData?.frontend_lead_id,
    openRowData?.backend_lead_id,
  ]);

  useEffect(() => {
    if (!openRowData || notesDirty || notesSaveInFlightRef.current) return;
    if (lastAppliedNoteSaveGenerationRef.current === noteSaveGenerationRef.current) return;
    setTaskNotes(openRowData.task_notes || "");
    setInvoicingNotes(openRowData.invoicing_notes || "");
  }, [openRowKey, openRowData?.task_notes, openRowData?.invoicing_notes, notesDirty]);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      if (height > 0) {
        const rounded = Math.ceil(height) + 8;
        setMeasuredPanelHeight((prev) => (prev === rounded ? prev : rounded));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [openPanelRowId]);

  function handleCopyPreviousWeekClick() {
    if (!weeklyPlanner || copyingPreviousWeek) return;
    setCopyConfirmOpen(true);
  }

  async function confirmCopyPreviousWeek() {
    setCopyConfirmOpen(false);
    await handleCopyPreviousWeekConfirm();
  }

  async function handleCellsChanged(rawChanges: unknown) {
    if (
      !plannerModuleActive(activeModule) ||
      !weeklyPlanner ||
      !gridContract.meta?.editable ||
      allocationSaving
    )
      return;
    const changes = rawChanges as Array<{
      rowId: string;
      columnId: string;
      newCell: { type: string; value?: number };
    }>;
    const allocationTargets = changes
      .map((change) => {
        const rowTarget = gridContract.meta?.plannerRowsById?.[String(change.rowId)];
        const developerId = gridContract.meta?.developerIdByColumnId?.[String(change.columnId)];
        const editableCellKey = `${String(change.rowId)}::${String(change.columnId)}`;
        const canEditThisCell = Boolean(gridContract.meta?.editableHourCellKeys?.[editableCellKey]);
        const nextValue = change.newCell?.value;
        if (!rowTarget || !developerId || !canEditThisCell || typeof nextValue !== "number")
          return null;
        // Guard invalid hour entries client-side before hitting planner APIs.
        if (nextValue < 0 || nextValue > 168) return null;
        return {
          rowTarget,
          developerId,
          roundedValue: Math.round(nextValue * 4) / 4,
        };
      })
      .filter(
        (
          target,
        ): target is {
          rowTarget: { row_kind: "project" | "roll_up_group"; row_entity_id: number };
          developerId: number;
          roundedValue: number;
        } => target !== null,
      );

    const availableTargets = changes
      .map((change) => {
        if (String(change.rowId) !== "available") return null;
        const developerId = gridContract.meta?.developerIdByColumnId?.[String(change.columnId)];
        const editableCellKey = `${String(change.rowId)}::${String(change.columnId)}`;
        const canEditThisCell = Boolean(gridContract.meta?.editableAvailableHoursCellKeys?.[editableCellKey]);
        const nextValue = change.newCell?.value;
        if (!developerId || !canEditThisCell || typeof nextValue !== "number") return null;
        return {
          developerId,
          roundedValue: Math.round(nextValue * 2) / 2,
        };
      })
      .filter((target): target is { developerId: number; roundedValue: number } => target !== null);

    if (allocationTargets.length === 0 && availableTargets.length === 0) return;

    setAllocationSaving(true);
    setPlannerError(null);
    try {
      for (const target of allocationTargets) {
        const payload = await saveWeeklyPlannerAllocation({
          week_start_on: weeklyPlanner.week_start_on,
          row_kind: target.rowTarget.row_kind,
          row_entity_id: target.rowTarget.row_entity_id,
          user_id: target.developerId,
          estimated_hours: target.roundedValue,
          view: plannerApiViewForModule(activeModule),
          include_archived: includeArchived,
          client_request_id: clientRequestIdRef.current,
        });
        setWeeklyPlanner(payload);
      }

      for (const target of availableTargets) {
        const payload = await saveWeeklyPlannerAvailableHours({
          week_start_on: weeklyPlanner.week_start_on,
          user_id: target.developerId,
          hours_available: target.roundedValue,
          view: plannerApiViewForModule(activeModule),
          include_archived: includeArchived,
          client_request_id: clientRequestIdRef.current,
        });
        setWeeklyPlanner(payload);
      }
    } catch (error) {
      setPlannerError(error instanceof Error ? error.message : "Failed to save hours");
    } finally {
      setAllocationSaving(false);
    }
  }

  async function handleRowsReordered(rawChanges: unknown) {
    if (!plannerModuleActive(activeModule) || !weeklyPlanner || !gridContract.meta?.editable) return;
    if (!(userRoles.includes("administrator") || userRoles.includes("project_manager"))) return;

    type ReorderChange = { rowId: string | number; targetRowId: string | number };
    const changes = Array.isArray(rawChanges) ? (rawChanges as ReorderChange[]) : [];
    const firstChange = changes[0];
    if (!firstChange) return;

    const sourceRowId = String(firstChange.rowId);
    const targetRowId = String(firstChange.targetRowId);
    if (sourceRowId === targetRowId) return;

    const { projectGroupId, nextOrder, changed } = nextProjectOrderForDrop({
      plannerRows: weeklyPlanner.rows,
      plannerRowsById: gridContract.meta?.plannerRowsById,
      sourceRowId,
      targetRowId,
      targetEdge: dropTargetEdge || "top",
    });
    if (!projectGroupId || !changed) return;

    setPlannerError(null);
    try {
      const payload = await saveWeeklyPlannerProjectOrder({
        week_start_on: weeklyPlanner.week_start_on,
        view: plannerApiViewForModule(activeModule),
        project_group_id: projectGroupId,
        project_ids: nextOrder,
        include_archived: includeArchived,
        client_request_id: clientRequestIdRef.current,
      });
      setWeeklyPlanner(payload);
    } catch (error) {
      setPlannerError(error instanceof Error ? error.message : "Failed to save project order");
    }
  }

  function handleProjectRowHandleDrop(targetRowId: string) {
    if (!draggedProjectRowId || draggedProjectRowId === targetRowId) return;
    void handleRowsReordered([{ rowId: draggedProjectRowId, targetRowId }]);
  }

  function clearProjectRowDragState() {
    setDraggedProjectRowId(null);
    setDropTargetProjectRowId(null);
    setDropTargetEdge(null);
  }

  async function handleDeveloperOrderDrop(targetDeveloperId: number) {
    if (!weeklyPlanner || !draggedDeveloperId || draggedDeveloperId === targetDeveloperId) return;

    const { nextOrder, changed } = nextDeveloperOrderForDrop({
      planner: weeklyPlanner,
      sourceDeveloperId: draggedDeveloperId,
      targetDeveloperId,
      targetEdge: dropTargetDeveloperEdge || "left",
    });
    if (!changed) return;

    setPlannerError(null);
    try {
      const payload = await saveWeeklyPlannerDeveloperOrder({
        week_start_on: weeklyPlanner.week_start_on,
        view: plannerApiViewForModule(activeModule),
        user_ids: nextOrder,
        include_archived: includeArchived,
        client_request_id: clientRequestIdRef.current,
      });
      setWeeklyPlanner(payload);
    } catch (error) {
      setPlannerError(error instanceof Error ? error.message : "Failed to save developer order");
    }
  }

  function clearDeveloperColumnDragState() {
    setDraggedDeveloperId(null);
    setDropTargetDeveloperId(null);
    setDropTargetDeveloperEdge(null);
  }

  function clearDeveloperDropTargetIfMatch(userId: number) {
    setDropTargetDeveloperId((current) => {
      if (current === userId) {
        setDropTargetDeveloperEdge(null);
        return null;
      }
      return current;
    });
  }

  function clearDropTargetIfMatch(rowId: string) {
    setDropTargetProjectRowId((current) => {
      if (current === rowId) {
        setDropTargetEdge(null);
        return null;
      }
      return current;
    });
  }

  function publishActiveCell(
    location: { rowId: string | number; columnId: string | number } | null,
  ) {
    if (!location) return;
    const { rowId, columnId } = location;
    if (typeof rowId !== "string" && typeof rowId !== "number") return;
    if (typeof columnId !== "string" && typeof columnId !== "number") return;

    publishActiveCellChanged(String(rowId), String(columnId));
  }

  function extractCellLocation(
    raw: unknown,
  ): { rowId: string | number; columnId: string | number } | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loc = raw as any;
    if (!loc) return null;
    if (loc.rowId !== undefined && loc.columnId !== undefined) {
      return { rowId: loc.rowId, columnId: loc.columnId };
    }
    return null;
  }

  function openPanelFromLocation(
    location: { rowId: string | number; columnId: string | number } | null,
  ) {
    if (!location) return;
    publishActiveCell(location);
    if (String(location.columnId) !== "project") return;
    const rowId = String(location.rowId);
    const rowTarget = gridContract.meta?.plannerRowsById?.[rowId];
    if (!rowTarget) return;
    setOpenPanelRowId((current) => {
      if (current === rowId) {
        setMeasuredPanelHeight(null);
        return null;
      }
      setMeasuredPanelHeight(WEEKLY_PLANNER_PANEL_HEIGHT_DEFAULT);
      return rowId;
    });
  }

  function handleFocusLocationChanged(raw: unknown) {
    openPanelFromLocation(extractCellLocation(raw));
  }

  function handleSelectionChanged(selection: unknown) {
    const location = firstLocationFromSelection(selection);
    if (!location) return;
    openPanelFromLocation(location);
  }

  async function persistRowNote() {
    if (!weeklyPlanner || !openRowTarget) return;
    if (notesSaveInFlightRef.current) {
      pendingNoteSaveRef.current = true;
      return;
    }

    const snapshot = {
      taskNotes,
      invoicingNotes,
      noteDueDate,
    };
    notesSaveInFlightRef.current = true;
    setNotesSaveState("saving");
    setPlannerError(null);
    try {
      const payload = await saveWeeklyPlannerRowNote({
        week_start_on: weeklyPlanner.week_start_on,
        row_kind: openRowTarget.row_kind,
        row_entity_id: openRowTarget.row_entity_id,
        due_date: noteDueDate || null,
        task_notes: taskNotes,
        invoicing_notes: invoicingNotes,
        view: plannerApiViewForModule(activeModule),
        include_archived: includeArchived,
        client_request_id: clientRequestIdRef.current,
      });
      noteSaveGenerationRef.current += 1;
      lastAppliedNoteSaveGenerationRef.current = noteSaveGenerationRef.current;
      setWeeklyPlanner(payload);
      const unchangedSinceSave =
        taskNotes === snapshot.taskNotes &&
        invoicingNotes === snapshot.invoicingNotes &&
        noteDueDate === snapshot.noteDueDate;
      if (unchangedSinceSave) {
        setNotesDirty(false);
      }
      setNotesSaveState("saved");
      window.setTimeout(() => setNotesSaveState("idle"), 1200);
    } catch (error) {
      setPlannerError(error instanceof Error ? error.message : "Failed to save row notes");
      setNotesSaveState("error");
      setNotesDirty(true);
    } finally {
      notesSaveInFlightRef.current = false;
      if (pendingNoteSaveRef.current) {
        pendingNoteSaveRef.current = false;
        void persistRowNote();
      }
    }
  }

  async function handleSyncClick() {
    if (notesDirty && openRowTarget) {
      await persistRowNote();
    }
    await handleSync();
  }

  async function saveProjectMetadata(patch: {
    projected_end_date?: string | null;
    project_owner_id?: number | null;
    project_manager_id?: number | null;
    frontend_lead_id?: number | null;
    backend_lead_id?: number | null;
  }) {
    if (!weeklyPlanner || !openRowTarget || openRowTarget.row_kind !== "project") return;
    setMetadataSaveState("saving");
    setPlannerError(null);
    try {
      const payload = await saveWeeklyPlannerProjectMetadata({
        row_entity_id: openRowTarget.row_entity_id,
        week_start_on: weeklyPlanner?.week_start_on,
        view: plannerApiViewForModule(activeModule),
        include_archived: includeArchived,
        client_request_id: clientRequestIdRef.current,
        ...patch,
      });
      setWeeklyPlanner(payload);
      setMetadataSaveState("saved");
      window.setTimeout(() => setMetadataSaveState("idle"), 1200);
    } catch (error) {
      setPlannerError(error instanceof Error ? error.message : "Failed to save project metadata");
      setMetadataSaveState("error");
    }
  }

  useEffect(() => {
    if (!notesDirty || !openRowTarget) return;
    const timeoutId = window.setTimeout(() => {
      void persistRowNote();
    }, 400);
    return () => window.clearTimeout(timeoutId);
  }, [
    notesDirty,
    taskNotes,
    invoicingNotes,
    noteDueDate,
    openRowTarget?.row_kind,
    openRowTarget?.row_entity_id,
    weeklyPlanner?.week_start_on,
  ]);

  const contextControls = plannerModuleActive(activeModule) ? (
    <Flex data-testid="planner-context-controls" className="w-full items-center">
      <Flex data-testid="planner-week-controls" className="flex w-full items-center gap-2">
        <Button
          aria-label="Go to previous week"
          size="2"
          variant="soft"
          className="text-xs sm:text-sm"
          onClick={() => setSelectedWeekStartOn(shiftWeek(selectedWeekStartOn, -1))}
        >
          {"← Prev Week"}
        </Button>
        <TextField.Root
          type="date"
          aria-label="Select week"
          value={selectedWeekStartOn}
          size="2"
          className="w-[11.5rem] shrink-0 text-xs sm:text-sm"
          onChange={(event) => setSelectedWeekStartOn(weekStartFromDateInput(event.target.value))}
        />
        <Button
          aria-label="Go to next week"
          size="2"
          variant="soft"
          className="text-xs sm:text-sm"
          onClick={() => setSelectedWeekStartOn(shiftWeek(selectedWeekStartOn, 1))}
        >
          {"Next Week →"}
        </Button>
      </Flex>
    </Flex>
  ) : null;

  const canCopyPreviousWeek =
    activeModule === "weekly_planner" &&
    Boolean(weeklyPlanner?.editable) &&
    (userRoles.includes("administrator") || userRoles.includes("project_manager"));

  const pageActions = plannerModuleActive(activeModule) ? (
    <Tooltip content="Sync Harvest/project data for the selected week and refresh planner metrics.">
      <Button size="2" className="text-xs sm:text-sm" onClick={handleSyncClick} disabled={syncing}>
        {syncing ? (
          <>
            <Spinner size="1" className="mr-2" />
            Syncing...
          </>
        ) : (
          "Sync"
        )}
      </Button>
    </Tooltip>
  ) : null;

  const weeklyPlannerActions = plannerModuleActive(activeModule) ? (
    <Flex data-testid="planner-actions-row" gap="2" className="w-full items-center">
      <Text size="1" color="gray" className="whitespace-nowrap">
        Week of {selectedWeekStartOn}
      </Text>
      {activeModule === "weekly_planner" ? (
        <Text as="label" size="1" className="flex items-center gap-2 whitespace-nowrap">
          <Checkbox
            checked={includeArchived}
            onCheckedChange={(checked) => setIncludeArchived(checked === true)}
            data-testid="show-archived-projects"
          />
          Show archived projects with assigned hours
        </Text>
      ) : null}
      {canCopyPreviousWeek ? (
        <Button
          size="2"
          variant="soft"
          className="text-xs sm:text-sm"
          onClick={handleCopyPreviousWeekClick}
          disabled={copyingPreviousWeek}
        >
          {copyingPreviousWeek ? "Copying..." : "Copy Previous Week"}
        </Button>
      ) : null}
      {pageActions}
    </Flex>
  ) : null;

  const showPlannerLoading =
    plannerModuleActive(activeModule) && !plannerError && (plannerFetching || !weeklyPlanner);

  return (
    <AppLayout
      user={auth.user}
      activeModule={activeModule}
      onModuleChange={setActiveModule}
      pageTitle={gridContract.title}
      contextControls={contextControls}
      pageActions={weeklyPlannerActions}
    >
      <AlertDialog.Root open={copyConfirmOpen} onOpenChange={setCopyConfirmOpen}>
        <AlertDialog.Content maxWidth="520px">
          <AlertDialog.Title>Copy Previous Week?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This will overwrite projected hours and row notes already entered for this week.
          </AlertDialog.Description>
          <Flex justify="end" gap="2" mt="4">
            <AlertDialog.Cancel>
              <Button variant="soft">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                onClick={() => {
                  void confirmCopyPreviousWeek();
                }}
                disabled={copyingPreviousWeek}
              >
                {copyingPreviousWeek ? "Copying..." : "Copy and Overwrite"}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
      <Box className="weekly-planner-grid h-[calc(100vh-180px)] overflow-auto border border-[var(--gray-6)]">
        {plannerError ? (
          <Text color="red" size="2">
            {plannerError}
          </Text>
        ) : null}
        {syncMessage ? (
          <Text color="green" size="2">
            {syncMessage}
          </Text>
        ) : null}
        {realtimeMessage ? (
          <Text color="indigo" size="2">
            {realtimeMessage}
          </Text>
        ) : null}
        {showPlannerLoading ? (
          <Flex
            align="center"
            justify="center"
            gap="2"
            className="min-h-[min(360px,calc(100vh-220px))] w-full rounded-lg border bg-background p-6"
          >
            <Spinner size="2" />
            <Text size="2" color="gray">
              Loading planner…
            </Text>
          </Flex>
        ) : (
          <>
            {plannerModuleActive(activeModule) && weeklyPlanner && !weeklyPlanner.editable ? (
              <Text color="amber" size="2">
                Past week is read-only. Move to current or future week to edit hours.
              </Text>
            ) : null}
            <Box className="weekly-planner-grid-canvas relative">
              <ReactGrid
                columns={gridContract.columns}
                rows={gridContract.rows}
                stickyTopRows={gridContract.stickyTopRows}
                stickyLeftColumns={gridContract.stickyLeftColumns}
                enableFillHandle={false}
                enableRangeSelection={false}
                disableVirtualScrolling
                onCellsChanged={handleCellsChanged}
                onRowsReordered={handleRowsReordered}
                onFocusLocationChanged={handleFocusLocationChanged}
                onSelectionChanged={handleSelectionChanged}
              />
              {canReorderProjectRows || canReorderDeveloperColumns ? (
                <Box className="pointer-events-none absolute inset-0 z-20">
                  {canReorderDeveloperColumns
                    ? developerColumnHandles.map((handle) => (
                        <button
                          key={`developer-column-handle-${handle.userId}`}
                          type="button"
                          aria-label={`Drag to reorder ${handle.label} column`}
                          title={`Drag to reorder ${handle.label} column`}
                          className={`weekly-planner-column-drag-handle pointer-events-auto absolute${
                            draggedDeveloperId === handle.userId ? " is-dragging" : ""
                          }`}
                          style={{
                            left: `${handle.left + 1}px`,
                            top: `${handle.top + 1}px`,
                            width: `${Math.max(handle.width - 2, 18)}px`,
                            height: `${Math.max(handle.height - 2, 18)}px`,
                          }}
                          draggable
                          onDragStart={() => {
                            setDraggedDeveloperId(handle.userId);
                            setDropTargetDeveloperId(null);
                            setDropTargetDeveloperEdge(null);
                          }}
                          onDragOver={(event) => {
                            event.preventDefault();
                            if (!draggedDeveloperId || draggedDeveloperId === handle.userId) return;

                            const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
                            const edge = edgeFromPoint({
                              axis: "horizontal",
                              point: event.clientX,
                              rectStart: rect.left,
                              rectSize: rect.width,
                            });
                            setDropTargetDeveloperId(handle.userId);
                            setDropTargetDeveloperEdge(edge);
                          }}
                          onDragLeave={() => {
                            clearDeveloperDropTargetIfMatch(handle.userId);
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            void handleDeveloperOrderDrop(handle.userId);
                            clearDeveloperColumnDragState();
                          }}
                          onDragEnd={clearDeveloperColumnDragState}
                        />
                      ))
                    : null}
                  {projectRowHandles.map((handle) => (
                    <button
                      key={`project-row-handle-${handle.rowId}`}
                      type="button"
                      aria-label={`Drag to reorder ${handle.rowLabel}`}
                      title={`Drag to reorder ${handle.rowLabel}`}
                      className="weekly-planner-row-drag-handle pointer-events-auto absolute left-0"
                      style={{
                        top: `${handle.top + 1}px`,
                        height: `${Math.max(handle.height - 2, 24)}px`,
                      }}
                      draggable
                      onDragStart={() => {
                        setDraggedProjectRowId(handle.rowId);
                        setDropTargetProjectRowId(null);
                        setDropTargetEdge(null);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        if (!draggedProjectRowId || draggedProjectRowId === handle.rowId) return;

                        const allowedDrop = canDropProjectRow({
                          plannerRowsById: gridContract.meta?.plannerRowsById,
                          sourceRowId: draggedProjectRowId,
                          targetRowId: handle.rowId,
                        });
                        if (!allowedDrop) {
                          setDropTargetProjectRowId(null);
                          setDropTargetEdge(null);
                          return;
                        }

                        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
                        const edge = edgeFromPoint({
                          axis: "vertical",
                          point: event.clientY,
                          rectStart: rect.top,
                          rectSize: rect.height,
                        });
                        setDropTargetProjectRowId(handle.rowId);
                        setDropTargetEdge(edge);
                      }}
                      onDragLeave={() => {
                        clearDropTargetIfMatch(handle.rowId);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        handleProjectRowHandleDrop(handle.rowId);
                        clearProjectRowDragState();
                      }}
                      onDragEnd={clearProjectRowDragState}
                    />
                  ))}
                </Box>
              ) : null}
              {openRowData && panelTop !== null ? (
                <PlannerInlinePanel
                  openRowData={openRowData}
                  panelTop={panelTop}
                  panelRef={panelRef}
                  panelRepositories={panelRepositories}
                  panelPullRequests={panelPullRequests}
                  panelCommits={panelCommits}
                  repositoryActivityLoading={repositoryActivityLoading}
                  repositoryActivityError={repositoryActivityError}
                  canEditPanelProjectMetadata={canEditPanelProjectMetadata}
                  leaderOptions={weeklyPlanner?.leader_options || []}
                  projectedEndDate={projectedEndDate}
                  projectOwnerId={projectOwnerId}
                  projectManagerId={projectManagerId}
                  frontendLeadId={frontendLeadId}
                  backendLeadId={backendLeadId}
                  metadataSaveState={metadataSaveState}
                  taskNotes={taskNotes}
                  invoicingNotes={invoicingNotes}
                  notesDirty={notesDirty}
                  notesSaveState={notesSaveState}
                  onProjectedEndDateChange={(value) => {
                    setProjectedEndDate(value);
                    if (!canEditPanelProjectMetadata) return;
                    void saveProjectMetadata({ projected_end_date: value || null });
                  }}
                  onProjectOwnerChange={(value) => {
                    setProjectOwnerId(value);
                    void saveProjectMetadata({ project_owner_id: value ? Number(value) : null });
                  }}
                  onProjectManagerChange={(value) => {
                    setProjectManagerId(value);
                    void saveProjectMetadata({ project_manager_id: value ? Number(value) : null });
                  }}
                  onFrontendLeadChange={(value) => {
                    setFrontendLeadId(value);
                    void saveProjectMetadata({ frontend_lead_id: value ? Number(value) : null });
                  }}
                  onBackendLeadChange={(value) => {
                    setBackendLeadId(value);
                    void saveProjectMetadata({ backend_lead_id: value ? Number(value) : null });
                  }}
                  onTaskNotesChange={(value) => {
                    setTaskNotes(value);
                    setNotesDirty(true);
                  }}
                  onInvoicingNotesChange={(value) => {
                    setInvoicingNotes(value);
                    setNotesDirty(true);
                  }}
                  onPersistRowNote={() => {
                    void persistRowNote();
                  }}
                  onOpenProjectSettings={(projectId: number) => {
                    window.location.assign(`/projects?focus_project_id=${projectId}`);
                  }}
                />
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </AppLayout>
  );
}

function initialModuleFromQuery(): ModuleKey {
  if (typeof window === "undefined") return "weekly_planner";
  const params = new URLSearchParams(window.location.search);
  const module = params.get("module");
  if (
    module === "my_projects" ||
    module === "weekly_planner" ||
    module === "effort_projections" ||
    module === "payment_planner" ||
    module === "cash_flow"
  ) {
    return module;
  }
  return "weekly_planner";
}

function firstLocationFromSelection(
  selection: unknown,
): { rowId: string | number; columnId: string | number } | null {
  if (!Array.isArray(selection) || selection.length === 0) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const range = selection[0] as any;
  if (!range) return null;

  // ReactGrid Range objects have .first.row.rowId / .first.column.columnId
  const firstRow = range.first?.row;
  const firstCol = range.first?.column;
  if (firstRow?.rowId !== undefined && firstCol?.columnId !== undefined) {
    return { rowId: firstRow.rowId, columnId: firstCol.columnId };
  }

  // Fallback: .rows[0].rowId / .columns[0].columnId
  const rowObj = range.rows?.[0];
  const colObj = range.columns?.[0];
  if (rowObj?.rowId !== undefined && colObj?.columnId !== undefined) {
    return { rowId: rowObj.rowId, columnId: colObj.columnId };
  }

  return null;
}
